import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Key,
  Plus,
  Loader2,
  Search,
  Save,
  Trash2,
  Send,
  Upload,
  FileText,
  Copy,
  CheckCircle2,
  AlertTriangle,
  X,
  Pencil,
  Download,
  FileSpreadsheet,
  ChevronDown,
  History,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { parseServerDate } from './utils/datetime';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const statusStyles = {
  unclaimed: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  claimed: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  revoked: 'bg-red-500/10 text-red-300 border-red-500/30',
};

function StatusBadge({ status }) {
  return (
    <span
      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-bold ${
        statusStyles[status] || 'bg-slate-700 text-slate-300 border-slate-600'
      }`}
    >
      {status}
    </span>
  );
}

function TierBadge({ tier }) {
  return (
    <span
      className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-bold ${
        tier === 'pro'
          ? 'bg-violet-500/10 text-violet-300 border-violet-500/30'
          : 'bg-sky-500/10 text-sky-300 border-sky-500/30'
      }`}
    >
      {tier}
    </span>
  );
}

function CopyButton({ value, testId }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
      className="text-slate-400 hover:text-white"
      title="Copy license code"
    >
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

/**
 * Trigger the browser's native download for a blob response from
 * `GET /admin/licenses/export`. Shared by the top-bar dropdown AND the
 * post-bulk-upload toast action, so marketplace staff get the same
 * file regardless of entry point.
 */
/**
 * Trigger the browser's native download for a blob response from
 * `GET /admin/licenses/export`. Shared by the top-bar dropdown AND the
 * post-bulk-upload toast action, so marketplace staff get the same
 * file regardless of entry point.
 */
function buildCurrentFilterParams(search, statusFilter, tierFilter) {
  const p = {};
  if (search.trim()) p.search = search.trim();
  if (statusFilter !== 'all') p.status = statusFilter;
  if (tierFilter !== 'all') p.tier = tierFilter;
  return p;
}

async function downloadBatch(batchId, format, extraParams = {}) {
  try {
    const params = { format, ...extraParams };
    if (batchId) params.batch_id = batchId;
    const res = await adminApi.get('/admin/licenses/export', {
      params,
      responseType: 'blob',
    });
    const cd = res.headers?.['content-disposition'] || '';
    const m = /filename="?([^"]+)"?/.exec(cd);
    const stamp = new Date().toISOString().replace(/[-:TZ.]/g, '').slice(0, 14);
    const fallback = `licenses-${stamp}.${format}`;
    const filename = m ? m[1] : fallback;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);

    const matched = Number(res.headers?.['x-total-matched'] || 0);
    const returned = Number(res.headers?.['x-row-count'] || 0);
    const cap = Number(res.headers?.['x-row-cap'] || 10000);
    if (matched > cap) {
      toast.warning(
        `Only the newest ${cap.toLocaleString('en-IN')} of ${matched.toLocaleString('en-IN')} licenses were included — narrow your filters to download the rest.`
      );
    } else {
      toast.success(`Downloaded ${returned.toLocaleString('en-IN')} licenses`);
    }
  } catch (err) {
    const d = err?.response?.data?.detail;
    toast.error(typeof d === 'string' ? d : 'Download failed');
  }
}

function ManualCreateForm({ onCreated }) {
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [tier, setTier] = useState('standard');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) {
      toast.error('Order ID is required');
      return;
    }
    setSubmitting(true);
    try {
      const body = { order_id: orderId.trim(), tier };
      if (email.trim()) body.customer_email = email.trim();
      const res = await adminApi.post('/admin/licenses', body);
      const sentEmail = email.trim();
      toast.success(
        sentEmail
          ? `License created. Email queued to ${sentEmail}.`
          : 'License created (no email sent — no address provided).'
      );
      setOrderId('');
      setEmail('');
      setTier('standard');
      onCreated?.(res.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="license-manual-form"
      className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
          <Plus className="w-4 h-4 text-amber-400" />
        </div>
        <div>
          <h2 className="text-white font-semibold">Manual License Creation</h2>
          <p className="text-xs text-slate-400">
            Leave email blank to only generate the code (no email will be sent).
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="space-y-1.5 md:col-span-1">
          <Label className="text-slate-300 text-xs uppercase tracking-wider">Order ID *</Label>
          <Input
            data-testid="license-manual-order"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="AMZN-IN-001"
            required
            className="bg-slate-800/50 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-1.5 md:col-span-2">
          <Label className="text-slate-300 text-xs uppercase tracking-wider">
            Customer Email <span className="text-slate-500 normal-case text-[10px]">(optional)</span>
          </Label>
          <Input
            data-testid="license-manual-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="buyer@example.com"
            className="bg-slate-800/50 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-300 text-xs uppercase tracking-wider">Tier *</Label>
          <Select value={tier} onValueChange={setTier}>
            <SelectTrigger
              data-testid="license-manual-tier"
              className="bg-slate-800/50 border-slate-700 text-white"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-white">
              <SelectItem value="standard">Standard (3 seats)</SelectItem>
              <SelectItem value="pro">Pro (10 seats)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button
          type="submit"
          disabled={submitting}
          data-testid="license-manual-submit"
          className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…
            </>
          ) : (
            <>
              <Plus className="w-4 h-4 mr-2" /> Generate license
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

function BulkUploadBox({ onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      const res = await adminApi.post('/admin/licenses/bulk-upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      const s = res.data.summary;
      const bid = res.data.batch_id;
      toast.success(
        `CSV processed — ${s.created} created, ${s.errors} error(s)`,
        bid && s.created > 0
          ? {
              duration: 12000,
              action: {
                label: 'Download XLSX',
                onClick: () => downloadBatch(bid, 'xlsx'),
              },
            }
          : undefined,
      );
      onUploaded?.(res.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div
      data-testid="license-bulk-upload"
      className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-lg bg-violet-500/10 border border-violet-500/30 flex items-center justify-center">
          <FileText className="w-4 h-4 text-violet-400" />
        </div>
        <div>
          <h2 className="text-white font-semibold">Bulk Upload (CSV)</h2>
          <p className="text-xs text-slate-400">
            Columns required: <code className="text-slate-300">order_id, email, tier</code>. Emails are optional per row.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handlePick}
          className="hidden"
          data-testid="license-bulk-file"
        />
        <Button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="license-bulk-upload-btn"
          className="bg-violet-500 hover:bg-violet-400 text-slate-950 font-semibold"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" /> Upload CSV
            </>
          )}
        </Button>
        <a
          href="data:text/csv;charset=utf-8,order_id,email,tier%0AAMZN-101,buyer1@example.com,standard%0AFLIP-102,buyer2@example.com,pro%0AOFFLINE-103,,standard"
          download="license_bulk_sample.csv"
          className="text-xs text-slate-400 hover:text-slate-200 underline-offset-2 hover:underline"
        >
          Download sample CSV
        </a>
      </div>

      {result && (
        <div className="mt-5 bg-slate-950/60 rounded-xl border border-slate-800 p-4">
          <div className="flex items-center gap-4 mb-3 text-sm">
            <span className="text-emerald-400 flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> {result.summary.created} created
            </span>
            <span className="text-red-400 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" /> {result.summary.errors} error(s)
            </span>
            <span className="text-slate-400">{result.summary.total_rows} total rows</span>
          </div>
          {result.errors.length > 0 && (
            <ul className="space-y-1 text-xs text-red-300">
              {result.errors.map((e, i) => (
                <li key={i}>
                  Row {e.row}: {e.error}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function InlineEditable({ value, onSave, type = 'text', options, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(value ?? ''), [value]);

  const commit = async () => {
    if (draft === (value ?? '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch {
      /* toast shown upstream */
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={testId}
        className="group inline-flex items-center gap-1.5 text-left text-slate-200 hover:text-white"
      >
        <span className="truncate max-w-[220px]">{value || <span className="text-slate-500 italic">(empty)</span>}</span>
        <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60" />
      </button>
    );
  }

  if (type === 'select') {
    return (
      <div className="flex items-center gap-1">
        <Select value={draft} onValueChange={setDraft}>
          <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-8 text-xs w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-white">
            {options.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button onClick={commit} className="text-emerald-400 hover:text-emerald-300 p-1" disabled={saving}>
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
        </button>
        <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-white p-1">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Input
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoFocus
        className="bg-slate-800 border-slate-700 text-white h-8 text-xs w-44"
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
      />
      <button onClick={commit} className="text-emerald-400 hover:text-emerald-300 p-1" disabled={saving}>
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
      </button>
      <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-white p-1">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function AdminLicensesPage() {
  const { admin, loading } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  // Captured from the most recent bulk-upload response — powers both the
  // post-upload toast action AND the "Last bulk upload" dropdown item so
  // marketplace staff can always grab the exact batch they just created.
  const [lastBatchId, setLastBatchId] = useState(null);
  const [lastBatchSize, setLastBatchSize] = useState(0);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const params = { page, page_size: 25 };
      if (search.trim()) params.search = search.trim();
      if (statusFilter !== 'all') params.status = statusFilter;
      if (tierFilter !== 'all') params.tier = tierFilter;
      const r = await adminApi.get('/admin/licenses', { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch {
      toast.error('Could not load licenses');
    } finally {
      setListLoading(false);
    }
  }, [page, search, statusFilter, tierFilter]);

  useEffect(() => {
    if (admin) fetchList();
  }, [admin, fetchList]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const updateField = async (id, update) => {
    try {
      const r = await adminApi.patch(`/admin/licenses/${id}`, update);
      setItems((prev) => prev.map((x) => (x.id === id ? r.data : x)));
      toast.success('License updated');
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Update failed');
      throw err;
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm('Delete this license? (Only unclaimed licenses can be deleted.)')) return;
    try {
      await adminApi.delete(`/admin/licenses/${id}`);
      toast.success('License deleted');
      fetchList();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Delete failed');
    }
  };

  const resendEmail = async (id) => {
    try {
      const r = await adminApi.post(`/admin/licenses/${id}/resend`);
      toast.success(`Email queued to ${r.data.to}`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Resend failed');
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / 25));

  return (
    <div
      data-testid="admin-licenses-page"
      className="min-h-screen"
      style={{
        background:
          'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)',
      }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link
            to="/admin"
            className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800"
            data-testid="admin-licenses-back"
          >
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <Key className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold">Licensing</div>
            <div className="text-xs text-slate-400">
              Manage license keys, deliveries, and bulk imports
            </div>
          </div>
          <Link
            to="/admin/license-logs"
            data-testid="admin-licenses-view-logs"
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-700 text-slate-200 hover:border-amber-500/40 hover:text-amber-300 transition"
          >
            <FileText className="w-3.5 h-3.5" /> Licensing Logs
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <ManualCreateForm onCreated={fetchList} />
          <BulkUploadBox
            onUploaded={(data) => {
              if (data?.batch_id && data?.summary?.created > 0) {
                setLastBatchId(data.batch_id);
                setLastBatchSize(data.summary.created);
              }
              fetchList();
            }}
          />
        </div>

        {/* License database */}
        <section className="bg-slate-900/70 border border-slate-800 rounded-2xl">
          <div className="p-5 border-b border-slate-800 flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex-1">
              <h2 className="text-white font-semibold">License Database</h2>
              <p className="text-xs text-slate-400">{total} total · click any Order ID / Email / Tier cell to edit inline</p>
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  data-testid="admin-licenses-search"
                  placeholder="Order / email / code"
                  value={search}
                  onChange={(e) => {
                    setPage(1);
                    setSearch(e.target.value);
                  }}
                  className="pl-9 bg-slate-800/50 border-slate-700 text-white w-64"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => { setPage(1); setStatusFilter(v); }}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-white">
                  <SelectItem value="all">All status</SelectItem>
                  <SelectItem value="unclaimed">Unclaimed</SelectItem>
                  <SelectItem value="claimed">Claimed</SelectItem>
                  <SelectItem value="revoked">Revoked</SelectItem>
                </SelectContent>
              </Select>
              <Select value={tierFilter} onValueChange={(v) => { setPage(1); setTierFilter(v); }}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-white">
                  <SelectItem value="all">All tiers</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    data-testid="admin-licenses-download-btn"
                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
                    title="Download licenses as XLSX / CSV"
                  >
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                    Download
                    <ChevronDown className="w-3 h-3 ml-1 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-72 bg-slate-900 border-slate-700 text-slate-100">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">
                    Current view ({total.toLocaleString('en-IN')} filtered)
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    data-testid="admin-licenses-download-view-xlsx"
                    onClick={() => downloadBatch(null, 'xlsx', buildCurrentFilterParams(search, statusFilter, tierFilter))}
                    disabled={total === 0}
                    className="focus:bg-slate-800 focus:text-white cursor-pointer"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-400" />
                    <div className="flex flex-col">
                      <span className="text-sm">Current view · Excel (.xlsx)</span>
                      <span className="text-[10px] text-slate-400">Respects status / tier / search filters</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="admin-licenses-download-view-csv"
                    onClick={() => downloadBatch(null, 'csv', buildCurrentFilterParams(search, statusFilter, tierFilter))}
                    disabled={total === 0}
                    className="focus:bg-slate-800 focus:text-white cursor-pointer"
                  >
                    <FileText className="w-4 h-4 mr-2 text-emerald-400" />
                    <div className="flex flex-col">
                      <span className="text-sm">Current view · CSV</span>
                      <span className="text-[10px] text-slate-400">UTF-8 with BOM — opens cleanly in Excel</span>
                    </div>
                  </DropdownMenuItem>
                  {lastBatchId && (
                    <>
                      <DropdownMenuSeparator className="bg-slate-700" />
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
                        <History className="w-3 h-3" /> Last bulk upload ({lastBatchSize.toLocaleString('en-IN')})
                      </DropdownMenuLabel>
                      <DropdownMenuItem
                        data-testid="admin-licenses-download-batch-xlsx"
                        onClick={() => downloadBatch(lastBatchId, 'xlsx')}
                        className="focus:bg-slate-800 focus:text-white cursor-pointer"
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-2 text-violet-400" />
                        <div className="flex flex-col">
                          <span className="text-sm">Last upload · Excel (.xlsx)</span>
                          <span className="text-[10px] text-slate-400 font-mono truncate">batch {lastBatchId.slice(0, 8)}…</span>
                        </div>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        data-testid="admin-licenses-download-batch-csv"
                        onClick={() => downloadBatch(lastBatchId, 'csv')}
                        className="focus:bg-slate-800 focus:text-white cursor-pointer"
                      >
                        <FileText className="w-4 h-4 mr-2 text-violet-400" />
                        <div className="flex flex-col">
                          <span className="text-sm">Last upload · CSV</span>
                          <span className="text-[10px] text-slate-400">Marketplace-ready keys only</span>
                        </div>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {listLoading ? (
            <div className="p-10 flex items-center gap-2 text-slate-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-400">
              No licenses yet. Create one above or upload a CSV.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                    <th className="px-5 py-3">Order ID</th>
                    <th className="px-5 py-3">Customer Email</th>
                    <th className="px-5 py-3">Tier</th>
                    <th className="px-5 py-3">License Code</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Created</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((lic) => (
                    <tr
                      key={lic.id}
                      data-testid={`admin-license-row-${lic.id}`}
                      className="border-b border-slate-800/80 last:border-b-0 hover:bg-slate-800/30"
                    >
                      <td className="px-5 py-3">
                        <InlineEditable
                          value={lic.order_id}
                          testId={`license-edit-order-${lic.id}`}
                          onSave={(v) => updateField(lic.id, { order_id: v })}
                        />
                      </td>
                      <td className="px-5 py-3">
                        <InlineEditable
                          value={lic.customer_email}
                          type="email"
                          testId={`license-edit-email-${lic.id}`}
                          onSave={(v) => updateField(lic.id, { customer_email: v })}
                        />
                      </td>
                      <td className="px-5 py-3">
                        <InlineEditable
                          value={lic.tier}
                          type="select"
                          options={['standard', 'pro']}
                          testId={`license-edit-tier-${lic.id}`}
                          onSave={(v) => updateField(lic.id, { tier: v })}
                        />
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <code className="text-xs text-slate-200 font-mono">{lic.license_code}</code>
                          <CopyButton value={lic.license_code} testId={`license-copy-${lic.id}`} />
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <StatusBadge status={lic.status} />
                      </td>
                      <td className="px-5 py-3 text-slate-400 text-xs">
                        {parseServerDate(lic.created_at)?.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) || ''}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex gap-1">
                          {lic.customer_email && (
                            <button
                              type="button"
                              title="Resend email"
                              onClick={() => resendEmail(lic.id)}
                              data-testid={`license-resend-${lic.id}`}
                              className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-700"
                            >
                              <Send className="w-3.5 h-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            title="Delete"
                            onClick={() => deleteOne(lic.id)}
                            data-testid={`license-delete-${lic.id}`}
                            className="p-1.5 rounded-md text-slate-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Pagination */}
              <div className="flex items-center justify-between px-5 py-3 border-t border-slate-800 text-xs text-slate-400">
                <span>Page {page} of {pageCount}</span>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white"
                  >
                    Prev
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    disabled={page >= pageCount}
                    className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
